import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import moment from "moment";

import placeholder from "../../assets/svgs/placeholder.svg";
import grocerySvg from "../../assets/svgs/Orders/grocery.svg";

import {
  HelpIconContainer,
  HelperTextPopup,
  HelperTextContent,
} from "../../styles/AddNewBundleDeal.styled";
import {
  WebContainer,
  WebWrapper,
  WebHeader,
  WebHeaderContent,
  WebTitle,
  WebTitleIcon,
  WebMainContent,
  WebLeftSection,
  WebRightSection,
  CustomTable,
  CustomTableHead,
  CustomTableRow,
  CustomTableHeader,
  CustomTableCell,
  CustomTableBody,
  TableItemContent,
  TableItemImage,
  TableItemDetails,
  TableItemName,
  TableItemIngredient,
  InfoCard,
  InfoCardTitle,
  InfoCardContent,
  InfoSection,
  InfoLabel,
  InfoValue,
  PaymentMethodCard,
  PaymentMethodText,
  WebSummaryContainer,
  WebSummaryContent,
  WebTotalRow,
  WebTotalValue,
  LoadingContainer,
  LoadingSpinner,
  ErrorContainer,
  ErrorMessage,
  PaymentMethodLabel,
  InfoSectionDeliverTo,
  FoodOptionsContainer,
  FoodOptionType,
  FoodOptionTypeName,
  FoodOptionsList,
  FoodOptionItem,
  FoodOptionName,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalFoodOptionsContainer,
  ModalFoodOptionType,
  ModalFoodOptionTypeName,
  ModalFoodOptionsList,
  ModalFoodOptionItem,
  ModalFoodOptionName,
  ModalTitleName,
} from "../../styles/OrderDetail.styled";

import { getObj } from "../../services/HTTPServices";
import { alertMessage } from "../../services/alertService";
import { SendButton } from "../../styles/AddNewFoodMenu.styled";
import { LanguageText } from "../../styles";
import { FlexContainer } from "../../styles/PersonalInfoStep";
import { ROUTES } from "../../constants";

// Custom Help Icon component to replace MUI HelpIcon
const HelpIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #cccccc;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 4px;

  &:before {
    content: "?";
  }
`;

interface OrderDetailState {
  order: any;
  loading: boolean;
  error: string | null;
  helperTextVisible: {
    [key: string]: boolean;
  };
  modalOpen: boolean;
  modalData: any;
}

const OrderDetailWeb: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<OrderDetailState>({
    order: null,
    loading: true,
    error: null,
    helperTextVisible: {},
    modalOpen: false,
    modalData: null,
  });

  const toggleHelperText = (key: string) => {
    setState((prev) => ({
      ...prev,
      helperTextVisible: {
        ...prev.helperTextVisible,
        [key]: !prev.helperTextVisible[key],
      },
    }));
  };

  const closeModal = () => {
    setState((prev) => ({ ...prev, modalOpen: false, modalData: null }));
  };

  useEffect(() => {
    if (id) {
      fetchOrderDetails(id);
    }
  }, [id]);

  const fetchOrderDetails = async (orderId: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      let res: any = await getObj("order/GetOrderById?id=", "order", orderId);
      if (res && res.data) {
        setTimeout(() => {
          setState((prev) => ({
            ...prev,
            order: res.data,
            loading: false,
          }));
        }, 500);
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: "Failed to load order details",
      }));
      alertMessage("Error!", "Failed to load order details");
    }
  };

  const calculateSubtotal = () => {
    if (!state.order?.orderFoodMenus) return 0;
    return state.order.orderFoodMenus.reduce(
      (sum: number, item: any) => sum + item.lineTotal,
      0
    );
  };

  if (state.loading) {
    return (
      <WebContainer>
        <WebWrapper>
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        </WebWrapper>
      </WebContainer>
    );
  }

  if (state.error || !state.order) {
    return (
      <WebContainer>
        <WebWrapper>
          <ErrorContainer>
            <ErrorMessage>Order not found or failed to load.</ErrorMessage>
          </ErrorContainer>
        </WebWrapper>
      </WebContainer>
    );
  }

  const { order } = state;
  const subtotal = calculateSubtotal();

  return (
    <WebContainer>
      <WebWrapper>
        <WebHeader>
          <WebHeaderContent>
            <WebTitleIcon src={grocerySvg} alt="Grocery Cart" />

            <WebTitle>Order Details</WebTitle>
          </WebHeaderContent>
        </WebHeader>

        <CustomTable>
          <CustomTableHead>
            <CustomTableRow>
              <CustomTableHeader>Item</CustomTableHeader>
              <CustomTableHeader>Qty</CustomTableHeader>
              <CustomTableHeader>Price</CustomTableHeader>
              <CustomTableHeader>Total</CustomTableHeader>
            </CustomTableRow>
          </CustomTableHead>
          <CustomTableBody>
            {order.orderFoodMenus?.map((item: any, index: number) => (
              <CustomTableRow key={item.id || index}>
                <CustomTableCell>
                  <TableItemContent>
                    <TableItemImage
                      src={item.photoPath || placeholder}
                      alt={item.item}
                    />
                    <TableItemDetails>
                      <TableItemName>{item.item}</TableItemName>
                      <TableItemIngredient>
                        {item.ingredient}
                      </TableItemIngredient>
                      {item.foodOptionsTypes &&
                        item.foodOptionsTypes.length > 0 && (
                          <FoodOptionsContainer>
                            {item.foodOptionsTypes.map(
                              (optionType: any, index: number) => (
                                <FoodOptionType
                                  key={optionType.foodOptionsTypeId || index}
                                >
                                  <FoodOptionTypeName className="crop_text_cart_150">
                                    {optionType.name}
                                  </FoodOptionTypeName>
                                  <FoodOptionsList>
                                    {optionType.foodOptions?.map(
                                      (option: any, optionIndex: number) => (
                                        <FoodOptionItem
                                          key={
                                            option.foodOptionsId ||
                                            optionIndex
                                          }
                                        >
                                          <FoodOptionName>
                                            {option.name} &nbsp; - &nbsp; {option.count} {option.unit || 'pc'}
                                          </FoodOptionName>
                                          
                                        </FoodOptionItem>
                                      )
                                    )}
                                  </FoodOptionsList>
                                </FoodOptionType>
                              )
                            )}
                          </FoodOptionsContainer>
                        )}
                    </TableItemDetails>
                  </TableItemContent>
                </CustomTableCell>
                <CustomTableCell>{item.qty}</CustomTableCell>
                <CustomTableCell>$ {item.price.toFixed(2)}</CustomTableCell>
                <CustomTableCell>$ {item.lineTotal.toFixed(2)}</CustomTableCell>
              </CustomTableRow>
            ))}
          </CustomTableBody>
        </CustomTable>

        <WebMainContent>
          <WebLeftSection>
            <InfoCard>
              <InfoCardContent>
                <InfoSection>
                  <InfoLabel> Order No.</InfoLabel>
                  <InfoValue>{order.orderNo}</InfoValue>
                </InfoSection>

                <InfoSection>
                  <InfoLabel>Order Time</InfoLabel>
                  <InfoValue>
                    {moment(order.orderDate).format("DD/MM/YYYY h:mm A")}
                  </InfoValue>
                </InfoSection>

                <InfoSection>
                  <InfoLabel>Delivery Buddy</InfoLabel>
                  <InfoValue>{order.courierName}</InfoValue>
                </InfoSection>
                <InfoSection>
                  <InfoLabel>Used Voucher Codes</InfoLabel>
                  <InfoValue>
                    {order.voucherCode || "No Voucher Code was used."}
                  </InfoValue>
                </InfoSection>

                <InfoSectionDeliverTo>
                  <InfoLabel>Deliver To</InfoLabel>
                  <InfoValue>{order.deliverTo || "-"}</InfoValue>
                </InfoSectionDeliverTo>
              </InfoCardContent>
            </InfoCard>
          </WebLeftSection>
          <WebRightSection>
            {/* Payment Methods */}

            <WebSummaryContainer>
              <WebSummaryContent>
                <InfoCardTitle>Payments</InfoCardTitle>

                <PaymentMethodCard>
                  <PaymentMethodLabel>Pay with</PaymentMethodLabel>
                  <PaymentMethodText>{order.paymentMethod}</PaymentMethodText>
                </PaymentMethodCard>

                <WebTotalRow>
                  <PaymentMethodLabel>Subtotal</PaymentMethodLabel>
                  <PaymentMethodText>$ {subtotal.toFixed(2)}</PaymentMethodText>
                </WebTotalRow>
                <WebTotalRow>
                  <PaymentMethodLabel>
                    Merchant Delivery Fee{" "}
                    <HelpIconContainer
                      className="help-icon-container"
                      onMouseOver={() => toggleHelperText("bundlePhoto")}
                      onMouseOut={() => toggleHelperText("bundlePhoto")}
                    >
                      <HelpIcon />
                      {state.helperTextVisible.bundlePhoto && (
                        <HelperTextPopup
                          $visible={state.helperTextVisible.bundlePhoto}
                        >
                          <HelperTextContent>
                            Merchant Delivery Fee
                          </HelperTextContent>
                        </HelperTextPopup>
                      )}
                    </HelpIconContainer>
                  </PaymentMethodLabel>
                  <PaymentMethodText>
                    $ {order.spDeliveryFees?.toFixed(2) || ""}
                  </PaymentMethodText>
                </WebTotalRow>
                <WebTotalRow>
                  <PaymentMethodLabel>Discount</PaymentMethodLabel>
                  <PaymentMethodText>
                    - $ {order.discount?.toFixed(2) || ""}
                  </PaymentMethodText>
                </WebTotalRow>
                <WebTotalRow>
                  <WebTotalValue>You'll receive</WebTotalValue>
                  <WebTotalValue>
                    $ {(subtotal - order.discount - order.spDeliveryFees)?.toFixed(2) || ""}
                  </WebTotalValue>
                </WebTotalRow>
              </WebSummaryContent>
            </WebSummaryContainer>
          </WebRightSection>

          
        </WebMainContent>
        <br></br>
      <SendButton onClick={() => navigate(ROUTES.ORDERS)}>
        <LanguageText>Back</LanguageText>
      </SendButton>
      </WebWrapper>

      {state.modalOpen && (
        <ModalOverlay $visible={state.modalOpen} onClick={closeModal}>
          <ModalContent
            $visible={state.modalOpen}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <FlexContainer>
                <ModalTitle>All Food Options</ModalTitle> -
                <ModalTitleName>{state.modalData?.item}</ModalTitleName>
              </FlexContainer>

              <ModalCloseButton onClick={closeModal}>×</ModalCloseButton>
            </ModalHeader>
            <ModalFoodOptionsContainer>
              {state.modalData?.foodOptionsTypes?.map(
                (optionType: any, index: number) => (
                  <ModalFoodOptionType
                    key={optionType.foodOptionsTypeId || index}
                  >
                    <ModalFoodOptionTypeName>
                      {optionType.name}
                    </ModalFoodOptionTypeName>
                    <ModalFoodOptionsList>
                      {optionType.foodOptions?.map(
                        (option: any, optionIndex: number) => (
                          <ModalFoodOptionItem
                            key={option.foodOptionsId || optionIndex}
                          >
                            <ModalFoodOptionName>
                              {option.name} &nbsp; - &nbsp;{option.count} {option.unit || 'pc'}
                            </ModalFoodOptionName>
                           
                          </ModalFoodOptionItem>
                        )
                      )}
                    </ModalFoodOptionsList>
                  </ModalFoodOptionType>
                )
              )}
            </ModalFoodOptionsContainer>
          </ModalContent>
        </ModalOverlay>
      )}
    </WebContainer>
  );
};

export default OrderDetailWeb;
